import { useEffect, useRef, useState } from "react";
import UserService from "rc-app/services/UserService";
import { startVrSession, completeVrSession } from "rc-app/api/vrSessionApi";
import { fetchFreshUserGeo } from "rc-app/utils/userGeoCache";
import {
  SessionState,
  createSession,
  buildStartPayload,
  buildCompletePayload,
} from "./vrSessionUtils";

export const MATTERPORT_SDK_KEY = "577fwy6piz72zr2zt46q4d8ea";
const MATTERPORT_SDK_URL = "https://static.matterport.com/showcase-sdk/3.6.0/sdk-bundle.js";
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const COMPLETE_URL = "/.netlify/functions/vr-session-complete";
const SESSION_STORAGE_PREFIX = "vrSession:inflight:";
// Skip orphans newer than this — they may still be live in another tab.
const ORPHAN_STALE_MS = 60 * 1000;

// Approximate viewport area in km² from Google's NE/SW corners.
const viewportAreaKm2 = (v: any): number | null => {
  if (!v?.northeast || !v?.southwest) return null;
  const dLat = (v.northeast.lat - v.southwest.lat) * 111;
  const midLat = (v.northeast.lat + v.southwest.lat) / 2;
  const dLng =
    (v.northeast.lng - v.southwest.lng) *
    111 *
    Math.cos((midLat * Math.PI) / 180);
  return Math.abs(dLat * dLng);
};

// IP-based geolocation, cached for the lifetime of the page load.
// Fired once, reused across all session creations.
//
// Two-step pipeline:
//   1. freeipapi.com — gives lat/lng + a city guess (often a hyperlocal
//      neighbourhood, not the city — e.g. "Gaddi Annaram" instead of
//      "Hyderabad"), free + no key + CORS-enabled.
//   2. Google reverse-geocoding the lat/lng → normalises to the proper
//      city ("locality" component), so analytics aggregate cleanly.
//
// If step 2 fails (no API key, network error), we fall back to step 1.
// If step 1 fails, sessions are created without geo (same as before).
const reverseGeocodeCity = async (
  lat: number,
  lng: number,
): Promise<string | null> => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data?.status !== "OK" || !Array.isArray(data?.results)) return null;
    // Prefer the locality from the LAST result that carries one.
    // Google orders results most-specific → most-general; for metro
    // suburbs like Manikonda the early results name the suburb but a
    // later one names the proper city (Hyderabad).
    const localityResults = data.results.filter((r: any) =>
      (r.address_components || []).some((c: any) =>
        c.types?.includes("locality"),
      ),
    );
    const cityResult = localityResults[localityResults.length - 1];
    let city: string | null = null;
    if (cityResult) {
      const locality = (cityResult.address_components || []).find((c: any) =>
        c.types?.includes("locality"),
      );
      if (locality?.long_name) city = locality.long_name;
    }
    // Peri-urban villages (e.g. Avilali outside Tirupati) get tagged
    // by Google as their own locality with a tiny viewport. When that
    // happens, broaden to admin_area_level_3 — in India that's
    // typically the surrounding city/revenue division ("Tirupati").
    const cityVpKm2 = viewportAreaKm2(cityResult?.geometry?.viewport);
    if (cityVpKm2 !== null && cityVpKm2 < 50) {
      const aa3Result = data.results.find(
        (r: any) =>
          Array.isArray(r.types) &&
          r.types.includes("administrative_area_level_3"),
      );
      const aa3 =
        aa3Result &&
        (aa3Result.address_components || []).find((c: any) =>
          c.types?.includes("administrative_area_level_3"),
        );
      if (aa3?.long_name && aa3.long_name !== city) city = aa3.long_name;
    }
    if (city) return city;
    // Fallback: admin_area_level_2 if no locality-typed result exists.
    for (const result of data.results) {
      const adminArea = result.address_components?.find((c: any) =>
        c.types?.includes("administrative_area_level_2"),
      );
      if (adminArea?.long_name) return adminArea.long_name;
    }
    return null;
  } catch {
    return null;
  }
};

let cachedGeoPromise: Promise<Record<string, any>> | null = null;
// Exported for unit tests; production code calls it via startSession only.
export const getClientGeo = (): Promise<Record<string, any>> => {
  if (cachedGeoPromise) return cachedGeoPromise;
  cachedGeoPromise = (async () => {
    // Prefer the authenticated user's GPS-based location (captured at
    // login via promptUserGeo). Travellers' sessions reflect their
    // current city automatically without any further interaction.
    // Returns null silently if consent isn't granted — we fall through
    // to the IP-based path below.
    try {
      const userGeo = await fetchFreshUserGeo();
      if (userGeo?.city) {
        return {
          city: userGeo.city,
          region: userGeo.region,
          country: userGeo.country,
          country_code: userGeo.country_code,
          latitude: userGeo.latitude,
          longitude: userGeo.longitude,
        };
      }
    } catch {
      // fall through to IP
    }
    try {
      const res = await fetch("https://free.freeipapi.com/api/json/");
      const data = await res.json();
      if (!data) return {};
      let city: string | null = data.cityName || null;
      // Normalise hyperlocal neighbourhood names to city-level via Google.
      if (typeof data.latitude === "number" && typeof data.longitude === "number") {
        const normalised = await reverseGeocodeCity(data.latitude, data.longitude);
        if (normalised) city = normalised;
      }
      return {
        city,
        region: data.regionName || null,
        country: data.countryName || null,
        country_code: data.countryCode || null,
        latitude: typeof data.latitude === "number" ? data.latitude : null,
        longitude: typeof data.longitude === "number" ? data.longitude : null,
      };
    } catch {
      return {};
    }
  })();
  return cachedGeoPromise;
};

// Cross-context dedup signal: returns true if localStorage still has the
// inflight record for this session. The record gets cleared by whichever
// path completes the session first (per-tour cleanup, unload beacon, or
// the global reaper). If it's gone, someone else already POSTed complete
// — the caller should skip the duplicate.
//
// Exported for unit tests; production code calls it via endSession /
// endSessionUnload.
export const isSessionStillInflight = (sessionId: string): boolean => {
  try {
    return localStorage.getItem(SESSION_STORAGE_PREFIX + sessionId) !== null;
  } catch {
    // localStorage unavailable — assume inflight (better to risk a rare
    // duplicate than to silently drop a real completion).
    return true;
  }
};

// Beacon path for unload-time completes — `fetch` gets killed during
// pagehide/visibilitychange=hidden, but `sendBeacon` and keepalive
// fetch survive. Returns true if the browser accepted the request.
const sendCompleteBeacon = (payload: Record<string, any>): boolean => {
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(COMPLETE_URL, blob)) return true;
    }
  } catch {
    // fall through to keepalive fetch
  }
  try {
    fetch(COMPLETE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
};

// Share-link code from the parent page URL (`?ref=<code>`). Used to blank the
// role IDs on both start AND complete for share sessions (attribution flows via
// referrer_share_code only). null on platform/organic visits.
const readShareCodeFromUrl = (): string | null => {
  try {
    // Long URL (?ref=<code>) OR short URL (/s/<code>). The complete + orphan
    // paths use this too, so it MUST match both — otherwise a /s/ session is
    // detected as shared on start (IDs blanked) but NOT on complete, and the
    // complete payload re-stamps aba_id/asa_id/agent_id/customer_id.
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) return ref;
    const m = window.location.pathname.match(/\/s\/([^/?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
};

interface InflightRecord {
  sessionId: string;
  matterportSid: string | null;
  agencyId: string;
  clientManagerId: string;
  clientAdminId: string;
  agentId: string;
  customerId: string;
  // Persisted so the orphan-recovery reaper (which runs on a later mount, when
  // the URL may no longer carry ?ref) still completes a share session with the
  // role IDs blanked. null for platform/organic sessions.
  shareCode: string | null;
  lastActivityMs: number;
  state: Omit<SessionState, "zonesVisited"> & { zonesVisited: string[] };
}

// One-shot guard so orphan recovery runs once per page load even if
// multiple components mount the hook.
let orphanRecoveryDone = false;

// Exported so the global session reaper (useGlobalSessionReaper) can call
// it on a timer from a top-level always-mounted component, catching
// sessions that the per-tour idle timer missed because the user navigated
// away from the tour (unmounting the timer's host).
export const recoverOrphanSessions = (staleThresholdMs = ORPHAN_STALE_MS) => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(SESSION_STORAGE_PREFIX)) keys.push(k);
    }
    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) {
          localStorage.removeItem(key);
          continue;
        }
        const orphan = JSON.parse(raw) as InflightRecord;
        // Skip records that are still being actively touched — the owning
        // tab updates lastActivityMs on every sweep/mode change.
        if (Date.now() - (orphan.lastActivityMs || 0) < staleThresholdMs) continue;
        const state: SessionState = {
          ...orphan.state,
          zonesVisited: new Set(orphan.state.zonesVisited || []),
        };
        const payload = buildCompletePayload({
          sessionId: orphan.sessionId,
          matterport: { matterport_id: orphan.matterportSid || "" },
          agencyId: orphan.agencyId,
          clientManagerId: orphan.clientManagerId,
          clientAdminId: orphan.clientAdminId,
          agentId: orphan.agentId,
          customerId: orphan.customerId,
          shareCode: orphan.shareCode ?? null,
          state,
          endTime: orphan.lastActivityMs || Date.now(),
        });
        sendCompleteBeacon(payload);
        localStorage.removeItem(key);
        console.log("[VR session] recovered orphan", orphan.sessionId);
      } catch (err) {
        console.warn("[VR session] orphan recovery failed for", key, err);
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
    }
  } catch {
    // localStorage unavailable — skip
  }
};

let sdkLoaderPromise: Promise<void> | null = null;
const loadMatterportSdk = (): Promise<void> => {
  if ((window as any).MP_SDK) return Promise.resolve();
  if (sdkLoaderPromise) return sdkLoaderPromise;
  sdkLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = MATTERPORT_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkLoaderPromise = null;
      reject(new Error("Failed to load Matterport SDK"));
    };
    document.head.appendChild(script);
  });
  return sdkLoaderPromise;
};

interface SessionContext {
  matterport: any;
  projectId: string;
  agencyId: string;
  agencySlug: string;
  projectSlug: string;
  clientManagerId: string;
  clientAdminId: string;
  agentId: string;
  customerId: string;
  /** Website-embed placement key — see StartPayloadInput.embedKey. */
  embedKey?: string | null;
  /** The visitor's campaign tags, forwarded from the host page — see StartPayloadInput.utm. */
  utm?: Record<string, string> | null;
}

export function useMatterportAnalytics(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  active: boolean,
  ctx: SessionContext,
) {
  const sessionRef = useRef<SessionState>(createSession());
  // Live engagement signals exposed to the lead gate. `sweepsVisited` = distinct
  // scan points entered (drives "spaces explored" + the gate trigger);
  // `totalSweeps` = all scan points in the model, from the SDK's Sweep.data
  // collection; `sessionId` binds a captured lead to this VR session.
  const [sweepsVisited, setSweepsVisited] = useState(0);
  const [totalSweeps, setTotalSweeps] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const visitedSweepsRef = useRef<Set<string>>(new Set());
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const idleTimerRef = useRef<any>(null);

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      void endSession();
    }, IDLE_TIMEOUT_MS);
  };

  // Snapshot the live session to localStorage so a crashed/killed tab
  // can be recovered on next mount via recoverOrphanSessions().
  const persistInflight = () => {
    const s = sessionRef.current;
    if (!s.sessionId) return;
    const c = ctxRef.current;
    try {
      const record: InflightRecord = {
        sessionId: s.sessionId,
        matterportSid: s.matterportSid,
        agencyId: c.agencyId,
        clientManagerId: c.clientManagerId,
        clientAdminId: c.clientAdminId,
        agentId: c.agentId,
        customerId: c.customerId,
        shareCode: readShareCodeFromUrl(),
        lastActivityMs: Date.now(),
        state: {
          ...s,
          zonesVisited: Array.from(s.zonesVisited),
        },
      };
      localStorage.setItem(
        SESSION_STORAGE_PREFIX + s.sessionId,
        JSON.stringify(record),
      );
    } catch {
      // quota exceeded or localStorage disabled — best effort
    }
  };

  const clearInflight = (sessionId: string | null) => {
    if (!sessionId) return;
    try {
      localStorage.removeItem(SESSION_STORAGE_PREFIX + sessionId);
    } catch {
      // ignore
    }
  };

  const startSession = async () => {
    const c = ctxRef.current;
    if (!c.matterport?.matterport_id) return;
    if (sessionRef.current.sessionId) return;
    try {
      // Read share-link channel from URL params. Public-viewer iframe URL
      // is `?projectId=...&ref=<code>&src=<channel>`; agent-app and direct
      // visits have no `src` param → null (= "organic / unattributed").
      // Read share-link params from the parent page URL. On the platform
      // (no ?ref / ?src) both are null → organic. On a share link
      // (?ref=<code>&src=<channel>) ref is always present; src may or may not be.
      let shareSrc: string | null = null;
      let shareCode: string | null = null;
      let clickId: string | null = null;
      try {
        const params = new URLSearchParams(window.location.search);
        shareSrc = params.get("src");
        shareCode = params.get("ref");
        // Short-link route /s/<code> carries the code in the PATH, not ?ref.
        if (!shareCode) {
          const m = window.location.pathname.match(/\/s\/([^/?]+)/);
          if (m) shareCode = decodeURIComponent(m[1]);
        }
        // click_id is stashed by ProjectDetailsV3's /click handler, keyed by the
        // share code (same tab), so the session bundles under its originating click.
        if (shareCode) clickId = sessionStorage.getItem(`vr_click:${shareCode}`);
      } catch {
        // URL/storage parsing failed — leave as null
      }
      const basePayload = buildStartPayload({
        matterport: c.matterport,
        projectId: c.projectId,
        agencyId: c.agencyId,
        agencySlug: c.agencySlug,
        projectSlug: c.projectSlug,
        clientManagerId: c.clientManagerId,
        clientAdminId: c.clientAdminId,
        agentId: c.agentId,
        customerId: c.customerId,
        userEmail: UserService.getUseremail?.() || "",
        shareSrc,
        shareCode,
        clickId,
        embedKey: c.embedKey,
        utm: c.utm,
        referrer: document.referrer || "",
        isMobile: window.innerWidth < 768,
      });
      // Enrich with IP-based geolocation (city/region/country). Won't
      // block session creation if the lookup fails or stalls.
      const geo = await getClientGeo();
      const payload = { ...basePayload, ...geo };
      const res = await startVrSession(payload);
      sessionRef.current = {
        ...createSession(),
        sessionId: res?.session_id || null,
        matterportSid: c.matterport.matterport_id,
        startTime: Date.now(),
        zoneEnterTime: Date.now(),
      };
      persistInflight();
      setSessionId(res?.session_id || null);
      visitedSweepsRef.current = new Set();
      setSweepsVisited(0);
      console.log("[VR session] started", res?.session_id);
      resetIdleTimer();
    } catch (err) {
      console.error("[VR session] start failed:", err);
    }
  };

  const endSession = async () => {
    const s = sessionRef.current;
    if (!s.sessionId) return;
    const c = ctxRef.current;
    const sessionIdToEnd = s.sessionId;
    // Cross-context dedup: another path (global reaper, beacon, etc.)
    // may have already completed this session and cleared the inflight
    // record. Don't POST a duplicate that would overwrite the real
    // duration with a stale Date.now()-based one.
    if (!isSessionStillInflight(sessionIdToEnd)) {
      sessionRef.current = createSession();
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }
    const payload = buildCompletePayload({
      sessionId: sessionIdToEnd,
      matterport: { matterport_id: s.matterportSid || "" },
      agencyId: c.agencyId,
      clientManagerId: c.clientManagerId,
      clientAdminId: c.clientAdminId,
      agentId: c.agentId,
      customerId: c.customerId,
      shareCode: readShareCodeFromUrl(),
      state: s,
      endTime: Date.now(),
    });
    sessionRef.current = createSession();
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    try {
      await completeVrSession(payload);
      console.log("[VR session] completed", sessionIdToEnd);
    } catch (err) {
      console.error("[VR session] complete failed:", err);
    } finally {
      clearInflight(sessionIdToEnd);
    }
  };

  // Synchronous complete path for unload events — fetch gets killed
  // during pagehide, sendBeacon survives. Clears localStorage so the
  // orphan recovery on the next mount won't re-fire this session.
  const endSessionUnload = () => {
    const s = sessionRef.current;
    if (!s.sessionId) return;
    const c = ctxRef.current;
    const sessionIdToEnd = s.sessionId;
    // Cross-context dedup (see endSession for rationale).
    if (!isSessionStillInflight(sessionIdToEnd)) {
      sessionRef.current = createSession();
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }
    const payload = buildCompletePayload({
      sessionId: sessionIdToEnd,
      matterport: { matterport_id: s.matterportSid || "" },
      agencyId: c.agencyId,
      clientManagerId: c.clientManagerId,
      clientAdminId: c.clientAdminId,
      agentId: c.agentId,
      customerId: c.customerId,
      shareCode: readShareCodeFromUrl(),
      state: s,
      endTime: Date.now(),
    });
    sendCompleteBeacon(payload);
    clearInflight(sessionIdToEnd);
    sessionRef.current = createSession();
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  // Main lifecycle: connect SDK, subscribe to events
  useEffect(() => {
    if (!active) return undefined;
    if (!ctx.matterport?.matterport_id) return undefined;
    const iframeEl = iframeRef.current;
    if (!iframeEl) return undefined;

    let cancelled = false;
    let mpSdk: any = null;
    const subscriptions: any[] = [];

    (async () => {
      try {
        await loadMatterportSdk();
        if (cancelled) return;
        mpSdk = await (window as any).MP_SDK.connect(iframeEl, MATTERPORT_SDK_KEY, "3.6");
        if (cancelled) {
          mpSdk.disconnect?.();
          return;
        }

        // Phase changes - fire start session on PLAYING
        subscriptions.push(
          mpSdk.App.state.subscribe((appState: any) => {
            if (appState.phase === mpSdk.App.Phase.PLAYING) {
              void startSession();
            }
          }),
        );

        // Sweep changes — we no longer track zone names for digital twin
        // sessions (matterport sweep UUIDs aren't human-readable and
        // resolving them to mattertag/floor labels turned out to be
        // unreliable). We still bump the node counter and reset idle.
        subscriptions.push(
          mpSdk.Sweep.current.subscribe((sweep: any) => {
            if (!sweep) return;
            sessionRef.current.totalNodes += 1;
            // Distinct scan points visited — the engagement signal the lead gate
            // counts ("spaces explored"), de-duped so bouncing between two spots
            // doesn't inflate it.
            const sid = String(sweep?.sid || sweep?.id || "");
            if (sid && !visitedSweepsRef.current.has(sid)) {
              visitedSweepsRef.current.add(sid);
              setSweepsVisited(visitedSweepsRef.current.size);
            }
            resetIdleTimer();
            persistInflight();
          }),
        );

        // Total scan points in the model (all sweeps) — the gate's "/N" ring
        // denominator. Defensive: Sweep.data / its shape varies by SDK version,
        // so the gate falls back to a count-only ring if this is unavailable.
        try {
          if (mpSdk.Sweep?.data?.subscribe) {
            subscriptions.push(
              mpSdk.Sweep.data.subscribe({
                onCollectionUpdated: (collection: any) => {
                  try {
                    const n =
                      typeof collection?.size === "number"
                        ? collection.size
                        : collection
                          ? Object.keys(collection).length
                          : 0;
                    if (n) setTotalSweeps(n);
                  } catch {
                    /* ignore */
                  }
                },
              }),
            );
          }
        } catch {
          /* Sweep.data not available on this SDK build */
        }

        // Mode changes - detect VR usage
        subscriptions.push(
          mpSdk.Mode.current.subscribe((mode: any) => {
            const isVr = mpSdk.Mode?.Mode && mode === mpSdk.Mode.Mode.VR;
            const s = sessionRef.current;
            if (isVr && !s.vrStartTime) {
              s.usedVr = true;
              s.vrStartTime = Date.now();
            } else if (!isVr && s.vrStartTime) {
              s.vrTotalTime += Date.now() - s.vrStartTime;
              s.vrStartTime = null;
            }
            persistInflight();
          }),
        );
      } catch (err) {
        console.error("[VR session] SDK setup failed:", err);
      }
    })();

    return () => {
      cancelled = true;
      subscriptions.forEach((sub) => sub?.cancel?.());
      if (sessionRef.current.sessionId) void endSession();
      if (mpSdk) mpSdk.disconnect?.();
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ctx.matterport?.matterport_id]);

  // Tab hidden / page unload — beacon path (fetch gets killed mid-flight
  // during unload, sendBeacon survives).
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden" && sessionRef.current.sessionId) {
        endSessionUnload();
      }
    };
    document.addEventListener("visibilitychange", handler);
    const pagehideHandler = () => {
      if (sessionRef.current.sessionId) endSessionUnload();
    };
    window.addEventListener("pagehide", pagehideHandler);
    return () => {
      document.removeEventListener("visibilitychange", handler);
      window.removeEventListener("pagehide", pagehideHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Orphan recovery — runs once per page load. Picks up any in-flight
  // session left behind by a crashed tab, force-quit, or sendBeacon
  // that the browser silently dropped, and fires complete for it.
  useEffect(() => {
    if (orphanRecoveryDone) return;
    orphanRecoveryDone = true;
    recoverOrphanSessions();
  }, []);

  return { sweepsVisited, totalSweeps, sessionId };
}
